class PdpVariantPicker extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('script[type="application/json"]');
    try {
      this.variants = JSON.parse(json?.textContent || "[]");
    } catch (e) {
      this.variants = [];
    }
    this.addEventListener("change", this.onChange);
  }

  disconnectedCallback() {
    this.removeEventListener("change", this.onChange);
  }

  onChange = () => {
    // Only options with >1 value render a fieldset; single-value options match every variant.
    // An option renders as radio pills (<=3 values) or a <select> (4+), so read whichever
    // control this fieldset actually contains.
    const picks = Array.from(this.querySelectorAll("fieldset[data-option-position]")).map((fs) => ({
      index: Number(fs.dataset.optionPosition) - 1,
      value: (fs.querySelector("select") || fs.querySelector("input:checked"))?.value,
    }));
    const variant = this.variants.find((v) =>
      picks.every(({ index, value }) => v.options[index] === value)
    );
    if (!variant) return;
    this.dispatchEvent(
      new CustomEvent("variant:change", { detail: { variant }, bubbles: true })
    );
  };
}

if (!customElements.get("pdp-variant-picker")) {
  customElements.define("pdp-variant-picker", PdpVariantPicker);
}

/*
 * <pdp-variant-refresh>
 *
 * Re-fetches sections/main-product.liquid through the Section Rendering API on
 * `variant:change` and replaces every [data-variant-refresh] element in the
 * DOCUMENT (not just its own subtree) by id.
 *
 * `pdp:refreshed` contract
 * ------------------------
 *   target : document
 *   detail : { variant }   // the variant object from the picker's JSON payload
 *   timing : after all regions are swapped and focus is restored, before setBusy(false)
 *
 * Rule for every other script in the theme: ANY code holding a reference to a node
 * inside a [data-variant-refresh] region must re-resolve that node on
 * `pdp:refreshed` — the old node is detached and its listeners are gone. Prefer a
 * delegated document listener; if you must cache a node, re-query it in the hook.
 * Regions today: _product-price, _product-variants, _product-stock,
 * _product-addtocart, and the fixed-bar wrapper in sections/main-product.liquid.
 */
class PdpVariantRefresh extends HTMLElement {
  connectedCallback() {
    this.addEventListener("variant:change", this.onVariantChange);
  }

  disconnectedCallback() {
    this.removeEventListener("variant:change", this.onVariantChange);
  }

  onVariantChange = async (event) => {
    const { variant } = event.detail;
    // No product URL (e.g. the section placed on a template without a product):
    // a fetch here would hit the current page and the fallback would navigate wrong.
    if (!variant || !this.dataset.url) return;

    // The radios are sr-only, so an arrow-key change leaves focus on a node the
    // swap is about to destroy. Remember it now, re-focus its replacement after.
    const focused = this.captureFocusedOption();

    this.controller?.abort();
    // Own the controller locally: by the time this handler's `finally` runs,
    // `this.controller` may already belong to a newer, still-in-flight request.
    const controller = new AbortController();
    this.controller = controller;
    this.setBusy(true);

    const url = `${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.sectionId}`;
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = new DOMParser().parseFromString(await res.text(), "text/html");
      // INVARIANT: no <script> inside a [data-variant-refresh] region will ever run
      // again after the first swap. Nodes adopted from DOMParser are inert — the
      // parser flags them "already started" — so neither inline nor src scripts
      // execute when replaceWith() inserts them. Anything a swapped region needs at
      // runtime must live in a document-level listener (see the pdp:refreshed
      // contract above) or a custom element, never in markup-local <script>.
      document.querySelectorAll("[data-variant-refresh]").forEach((el) => {
        const next = doc.getElementById(el.id);
        if (next) el.replaceWith(next);
      });
      this.restoreFocusedOption(focused);
      window.affirm?.ui?.refresh?.();
      document.dispatchEvent(
        new CustomEvent("pdp:refreshed", { detail: { variant } })
      );
    } catch (err) {
      if (err.name === "AbortError") return;
      window.location.href = `${this.dataset.url}?variant=${variant.id}`;
    } finally {
      if (!controller.signal.aborted) this.setBusy(false);
    }
  };

  captureFocusedOption() {
    const el = document.activeElement;
    if (!el || el.tagName !== "INPUT" || el.type !== "radio") return null;
    if (!el.name || !this.contains(el)) return null;
    return { name: el.name, value: el.value };
  }

  restoreFocusedOption(focused) {
    if (!focused) return;
    // Matched by property, not a built selector — option names/values are free
    // text and would need escaping inside an attribute selector.
    const next = Array.from(
      this.querySelectorAll('input[type="radio"]')
    ).find((el) => el.name === focused.name && el.value === focused.value);
    next?.focus({ preventScroll: true });
  }

  setBusy(busy) {
    if (busy) this.setAttribute("aria-busy", "true");
    else this.removeAttribute("aria-busy");
    // Query the DOCUMENT, not `this`. The fixed bar is a SIBLING of
    // <pdp-variant-refresh> (see sections/main-product.liquid) and carries its own
    // [data-variant-refresh]; scoping to `this` left its "Add to Bag" / "Buy Now"
    // live during the fetch, adding the PREVIOUS variant at the PREVIOUS price.
    // Every swapped region carries [data-variant-refresh], so this is exactly the
    // set of buttons about to be replaced.
    document.querySelectorAll("[data-variant-refresh] button").forEach((b) => {
      if (busy) {
        // Mark only the buttons WE disable. A button the server rendered disabled
        // (sold out, unavailable in market) must not be re-enabled on restore.
        if (b.hasAttribute("disabled")) return;
        b.setAttribute("data-refresh-disabled", "");
        b.setAttribute("disabled", "");
      } else if (b.hasAttribute("data-refresh-disabled")) {
        b.removeAttribute("data-refresh-disabled");
        b.removeAttribute("disabled");
      }
    });
  }
}

if (!customElements.get("pdp-variant-refresh")) {
  customElements.define("pdp-variant-refresh", PdpVariantRefresh);
}
